import Animated from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import { ActiveAlertsContainer } from '@/experiences/sales-portal/domain/insights/containers/active-alerts-container';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import { HiddenAlertsContainer } from '@/experiences/sales-portal/domain/insights/containers/hidden-alerts-container';
import { HubCardType } from '@/types';
import { INSIGHTS_MOBILE_LAYOUT_TRANSITION } from '@/experiences/sales-portal/domain/insights/components/constants';
import { InsightsMobileAlertFeed } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-alert-feed';
import { InsightsMobileAlertFeedSkeleton } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-alert-feed-skeleton';
import { InsightsMobileFeed } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-feed';
import { InsightsMobileHiddenAlerts } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-hidden-alerts';
import { InsightsMobileItemDetailPushView } from '@/experiences/sales-portal/domain/insights/components/insights-mobile-item-detail-push-view';
import { ItemDetailItemInfo } from '@/experiences/sales-portal/domain/items/blocs/types/item-detail-content';
import { LowestPerformingCustomersCard } from '@/experiences/sales-portal/domain/insights/components/lowest-performing-customers-card';
import { LowestPerformingCustomersCardSkeleton } from '@/experiences/sales-portal/domain/insights/components/lowest-performing-customers-card-skeleton';
import { ProfitRollupCard } from '@/experiences/sales-portal/domain/insights/components/profit-rollup-card';
import { ProfitRollupCardSkeleton } from '@/experiences/sales-portal/domain/insights/components/profit-rollup-card-skeleton';
import { SectionHeading, YStack } from '@usepepper/sprout-kit-ui';
import { useAccountDetailPress } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-account-detail-press';
import { useActiveAlertsLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-active-alerts-loader';
import { useAuth } from '@/contexts/auth-context';
import { useBottomNavScrollHandler } from '@sales-portal/blocs/hooks/use-bottom-nav-scroll-handler';
import { useHiddenAlertsLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-hidden-alerts-loader';
import {
  useInsightNavigation,
  useSelectedLostItemAlertUUID,
} from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-insight-navigation';
import { useLowestPerformingCustomersLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-lowest-performing-customers-loader';
import { useProfitRollupLoader } from '@/experiences/sales-portal/domain/insights/blocs/loaders/use-profit-rollup-loader';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';
import { useSalesRepConfig } from '@/experiences/sales-portal/blocs/hooks/use-sales-rep-config';
import type { LostItemAlert } from '@/experiences/sales-portal/domain/insights/blocs/types/alert';

interface InsightsMobileContentProps {
  supplierUUID: string;
}

export const InsightsMobileContent: React.FC<InsightsMobileContentProps> = ({
  supplierUUID,
}) => {
  const handleBottomNavScroll = useBottomNavScrollHandler();
  const openAccountDetail = useAccountDetailPress(supplierUUID);
  const { close } = useInsightNavigation();
  const selectedLostItemAlertUUID = useSelectedLostItemAlertUUID();
  const { timezone } = useSalesPortalSupplierMetadataLoader({ supplierUUID });

  const {
    alerts: activeAlerts,
    loading: activeAlertsLoading,
    error: activeAlertsError,
    refetch: refetchActiveAlerts,
  } = useActiveAlertsLoader();
  const {
    alerts: hiddenAlerts,
    loading: hiddenAlertsLoading,
    error: hiddenAlertsError,
  } = useHiddenAlertsLoader();

  const authenticatedUserUUID = useAuth((s) => s.authenticatedUserUUID);
  const { salesRepConfig, loading: salesRepConfigLoading } =
    useSalesRepConfig(supplierUUID);
  const isGrossProfitDisabled = salesRepConfig
    ? salesRepConfig.disable_gross_profit_display === true
    : true;

  const {
    data: profitRollupHubCardData,
    loading: profitRollupLoading,
    error: profitRollupError,
  } = useProfitRollupLoader(authenticatedUserUUID!, isGrossProfitDisabled);
  const {
    data: lowestPerformingCustomersHubCardData,
    loading: lowestPerformingCustomersLoading,
    error: lowestPerformingCustomersError,
  } = useLowestPerformingCustomersLoader(
    authenticatedUserUUID!,
    isGrossProfitDisabled,
  );

  const insightCardsPending = salesRepConfigLoading || !authenticatedUserUUID;
  const isProfitRollupCardLoading = insightCardsPending || profitRollupLoading;
  const isLowestPerformingCustomersCardLoading =
    insightCardsPending || lowestPerformingCustomersLoading;

  const showProfitRollupCard =
    isProfitRollupCardLoading ||
    Boolean(!profitRollupError && profitRollupHubCardData);
  const showLowestPerformingCustomersCard =
    isLowestPerformingCustomersCardLoading ||
    Boolean(
      !lowestPerformingCustomersError && lowestPerformingCustomersHubCardData,
    );
  const showInsightsSection =
    showProfitRollupCard || showLowestPerformingCustomersCard;

  const selectedLostItemAlert = useMemo<LostItemAlert | null>(
    () =>
      activeAlerts.find(
        (alert): alert is LostItemAlert =>
          alert.cardType === HubCardType.LostItem &&
          alert.alertUUID === selectedLostItemAlertUUID,
      ) ?? null,
    [activeAlerts, selectedLostItemAlertUUID],
  );

  const lostItemInfo = useMemo<ItemDetailItemInfo | null>(
    () =>
      selectedLostItemAlert
        ? {
            description: '',
            externalItemId: selectedLostItemAlert.externalItemId || null,
            imageUrl: selectedLostItemAlert.photoUrls?.[0] ?? null,
            itemUUID: null,
            metadata: null,
            name: selectedLostItemAlert.displayName,
            packDisplayString: selectedLostItemAlert.packUnit,
            upcList: [],
          }
        : null,
    [selectedLostItemAlert],
  );

  return (
    <>
      <AppPageLayout
        bodyGutter="$layoutGutterMobile"
        mode="focused"
        onBodyScroll={handleBottomNavScroll}
        scroll={false}
        title={i18n.t('insights_hub')}
      >
        <InsightsMobileFeed>
          <Animated.View layout={INSIGHTS_MOBILE_LAYOUT_TRANSITION}>
            <ActiveAlertsContainer
              alerts={activeAlerts}
              error={activeAlertsError}
              loading={activeAlertsLoading}
              refetch={refetchActiveAlerts}
              renderFeed={(feedProps) => (
                <InsightsMobileAlertFeed {...feedProps} />
              )}
              renderSkeleton={() => <InsightsMobileAlertFeedSkeleton />}
              supplierUUID={supplierUUID}
            />
          </Animated.View>
          <Animated.View layout={INSIGHTS_MOBILE_LAYOUT_TRANSITION}>
            <HiddenAlertsContainer
              activeAlertsLoading={activeAlertsLoading}
              alerts={hiddenAlerts}
              error={hiddenAlertsError}
              loading={hiddenAlertsLoading}
              renderSection={(sectionProps) => (
                <InsightsMobileHiddenAlerts {...sectionProps} />
              )}
              supplierUUID={supplierUUID}
            />
          </Animated.View>
          <Animated.View layout={INSIGHTS_MOBILE_LAYOUT_TRANSITION}>
            {showInsightsSection ? (
              <YStack gap="$8">
                <SectionHeading title={i18n.t('insights')} />
                {showProfitRollupCard ? (
                  isProfitRollupCardLoading ? (
                    <ProfitRollupCardSkeleton />
                  ) : (
                    <ProfitRollupCard data={profitRollupHubCardData!} />
                  )
                ) : null}
                {showLowestPerformingCustomersCard ? (
                  isLowestPerformingCustomersCardLoading ? (
                    <LowestPerformingCustomersCardSkeleton />
                  ) : (
                    <LowestPerformingCustomersCard
                      data={lowestPerformingCustomersHubCardData!}
                      onCustomerPress={openAccountDetail}
                    />
                  )
                ) : null}
              </YStack>
            ) : null}
          </Animated.View>
        </InsightsMobileFeed>
      </AppPageLayout>
      {selectedLostItemAlert && lostItemInfo ? (
        <InsightsMobileItemDetailPushView
          itemInfo={lostItemInfo}
          key={selectedLostItemAlert.alertUUID}
          onClose={close}
          open
          restaurantUUID={selectedLostItemAlert.restaurantUUID}
          supplierUUID={supplierUUID}
          timezone={timezone}
          variantPackUUID={selectedLostItemAlert.variantPackUUID}
        />
      ) : null}
    </>
  );
};
